import React, { useState, useCallback, useRef, useEffect, startTransition } from 'react';
import { Button } from '../../common/ui/button';
import { Input } from '../../common/ui/input';
import { Label } from '../../common/ui/label';
import { Textarea } from '../../common/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../common/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../common/ui/dialog';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  selectAdminProviders,
  fetchAdminProviders,
  fetchAdminDefaults,
  selectAdminDefaults,
} from '../../store/slices/adminSlice';
import { useNavigate } from 'react-router-dom';
import { selectUser } from '../../store/slices/authSlice';
import { selectSubscription } from '../../store/slices/subscriptionSlice';
import {
  validateAgentConfiguration,
  usesRootCredentials,
} from '../../utils/apiKeyValidation';
import { normalizePresetQuestions } from '../../services/normalizers';
import { AlertCircle, ExternalLink, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import ApiKeyValidationModal from '../Modals/ApiKeyValidationModal';
import { providersService, apiKeysService } from '../../services';
import { Switch } from '../../common/ui/switch';
import { Checkbox } from '../../common/ui/checkbox';
import { ConditionalField } from '../FeatureToggle/FeatureToggle';
import { useRecaptcha } from '../../hooks/useRecaptcha';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../common/ui/tabs';
import { useWorkspaceExperience } from '../../hooks/useWorkspaceExperience';

/** Ensure presetQuestions is always a string[] (API/DB may return object/string). */
function coercePresetQuestions(value) {
  return normalizePresetQuestions(value);
}

// Fallback when Core API is not reachable (e.g. ERR_CONNECTION_REFUSED) so provider dropdowns still work
const FALLBACK_PROVIDER_CATALOG = {
  ai: [
    {
      id: 'openai',
      provider: 'openai',
      type: 'ai',
      category: 'ai_models',
      name: 'OpenAI',
      models: [
        { id: 'gpt-4o-mini', name: 'GPT-4o Mini' },
        { id: 'gpt-4o', name: 'GPT-4o' },
      ],
    },
    {
      id: 'anthropic',
      provider: 'anthropic',
      type: 'ai',
      category: 'ai_models',
      name: 'Anthropic',
      models: [{ id: 'claude-3-5-sonnet-20241022', name: 'Claude 3.5 Sonnet' }],
    },
    {
      id: 'fireworks',
      provider: 'fireworks',
      type: 'ai',
      category: 'ai_models',
      name: 'Fireworks',
      models: [
        { id: 'accounts/fireworks/models/llama-v3p3-70b-instruct', name: 'Llama 3.3 70B Instruct' },
        { id: 'accounts/fireworks/models/llama-v3p1-70b-instruct', name: 'Llama 3.1 70B Instruct' },
        { id: 'accounts/fireworks/models/llama-v3p1-8b-instruct', name: 'Llama 3.1 8B Instruct' },
        { id: 'accounts/fireworks/models/llama-v3p2-3b-instruct', name: 'Llama 3.2 3B Instruct' },
        { id: 'accounts/fireworks/models/qwen2p5-72b-instruct', name: 'Qwen2.5 72B Instruct' },
        { id: 'accounts/fireworks/models/qwen2p5-32b-instruct', name: 'Qwen2.5 32B Instruct' },
        { id: 'accounts/fireworks/models/deepseek-v3', name: 'DeepSeek V3' },
        { id: 'accounts/fireworks/models/deepseek-r1', name: 'DeepSeek R1' },
        { id: 'accounts/fireworks/models/mixtral-8x22b-instruct', name: 'Mixtral 8x22B Instruct' },
        { id: 'accounts/fireworks/models/mixtral-8x7b-instruct', name: 'Mixtral 8x7B Instruct' },
        { id: 'accounts/fireworks/models/gemma2-9b-it', name: 'Gemma 2 9B IT' },
      ],
    },
  ],
  stt: [
    {
      id: 'deepgram',
      provider: 'deepgram',
      type: 'stt',
      category: 'stt_models',
      name: 'Deepgram',
      models: [
        { id: 'nova-2', name: 'Nova 2' },
        { id: 'nova-2-general', name: 'Nova 2 General' },
      ],
    },
    {
      id: 'openai',
      provider: 'openai',
      type: 'stt',
      category: 'stt_models',
      name: 'OpenAI Whisper',
      models: [{ id: 'whisper-1', name: 'Whisper 1' }],
    },
  ],
  tts: [
    {
      id: 'deepgram',
      provider: 'deepgram',
      type: 'tts',
      category: 'tts_models',
      name: 'Deepgram',
      models: [
        { id: 'aura-asteria-en', name: 'Aura Asteria' },
        { id: 'aura-luna-en', name: 'Aura Luna' },
      ],
    },
    {
      id: 'openai',
      provider: 'openai',
      type: 'tts',
      category: 'tts_models',
      name: 'OpenAI',
      models: [
        { id: 'alloy', name: 'Alloy' },
        { id: 'echo', name: 'Echo' },
      ],
    },
    {
      id: 'elevenlabs',
      provider: 'elevenlabs',
      type: 'tts',
      category: 'tts_models',
      name: 'ElevenLabs',
      models: [{ id: 'eleven_multilingual_v2', name: 'Multilingual v2' }],
    },
  ],
};

const AgentForm = ({
  isOpen,
  onClose,
  onSubmit,
  editingAgent = null,
  title = 'Create New Agent',
  isRootAgentMode = false, // When true, pre-check the root agent checkbox
}) => {
  const getRecaptchaToken = useRecaptcha(editingAgent ? 'update_agent' : 'create_agent');
  const { isBusiness, isAgency } = useWorkspaceExperience();
  const navigate = useNavigate();
  const [editorTab, setEditorTab] = useState('advanced');
  const dispatch = useAppDispatch();
  const adminProviders = useAppSelector(selectAdminProviders);
  const adminDefaults = useAppSelector(selectAdminDefaults) || {};
  const user = useAppSelector(selectUser);
  const subscription = useAppSelector(selectSubscription);
  const [providerCatalog, setProviderCatalog] = useState({ ai: [], tts: [], stt: [] });

  const skipUserApiKeyValidation = usesRootCredentials({
    user,
    subscription,
    isRootAgent:
      isRootAgentMode ||
      !!editingAgent?.isRootAgent ||
      false,
  });
  const getProvidersForCategory = category => {
    const typeMap = {
      ai: ['ai', 'ai_models'],
      tts: ['tts', 'tts_models'],
      stt: ['stt', 'stt_models'],
    };
    const validCategories = typeMap[category] || [category];

    const source = adminProviders.length ? adminProviders : providerCatalog[category] || [];

    return (source || []).filter(
      p => validCategories.includes(p.category) || validCategories.includes(p.type)
    );
  };

  const aiProviders = getProvidersForCategory('ai');
  const sttProviders = getProvidersForCategory('stt');
  const ttsProviders = getProvidersForCategory('tts');

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'super-admin') {
      dispatch(fetchAdminProviders());
      dispatch(fetchAdminDefaults());
    } else {
      // Fetch public providers for normal users
      (async () => {
        try {
          const data = await providersService.list();
          if (data.success && data.data) {
            // API returns flat list: { providers: [...] }
            const providers = data.data.providers || data.data || [];
            const grouped = {
              ai: providers.filter(p => p.type === 'ai' || p.category === 'ai_models'),
              stt: providers.filter(p => p.type === 'stt' || p.category === 'stt_models'),
              tts: providers.filter(p => p.type === 'tts' || p.category === 'tts_models'),
            };
            setProviderCatalog(grouped);
          } else if (
            data &&
            !data.success &&
            (data.error === 'Backend server not available' ||
              data.message === 'Cannot connect to server')
          ) {
            setProviderCatalog(FALLBACK_PROVIDER_CATALOG);
            toast(
              'Core API not reachable. Using default provider list. Start the API (e.g. run ./run-local.sh) for full list.',
              { icon: '⚠️' }
            );
          }
        } catch (e) {
          console.error('Failed to fetch providers:', e);
          setProviderCatalog(FALLBACK_PROVIDER_CATALOG);
          toast(
            'Could not load providers. Using defaults. Ensure Core API is running on port 4000 (e.g. ./run-local.sh).',
            { icon: '⚠️' }
          );
        }
      })();
    }
  }, [dispatch, user?.role]);

  const [formData, setFormData] = useState({
    name: editingAgent?.name || '',
    greeting: editingAgent?.greeting || '',
    persona: editingAgent?.persona || '',
    aiProvider: editingAgent?.aiProvider || '',
    aiModel: editingAgent?.aiModel || '',
    aiModelOverride: editingAgent?.aiModelOverride || '',
    voiceProvider: editingAgent?.voiceProvider || '',
    voiceId: editingAgent?.voiceId || '',
    ttsModelOverride: editingAgent?.ttsModelOverride || '',
    sttProvider: editingAgent?.sttProvider || '',
    sttModel: editingAgent?.sttModel || '',
    sttModelOverride: editingAgent?.sttModelOverride || '',
    sttLanguage: editingAgent?.sttLanguage || 'en',
    // New fields
    havePresetQuestions: editingAgent?.havePresetQuestions || false,
    presetQuestions: coercePresetQuestions(editingAgent?.presetQuestions),
    repromptMessage: editingAgent?.repromptMessage || "I didn't hear you. Could you please repeat?",
    useCase: editingAgent?.useCase || 'marketing',
    customUseCase: editingAgent?.customUseCase || '',
    enableRecording: editingAgent?.enableRecording || false,
    enableTranscriptions: editingAgent?.enableTranscriptions || false,
    isRootAgent: editingAgent?.isRootAgent || false,
    ttsConfig: editingAgent?.ttsConfig || { engine: 'neural' },
  });

  // API key validation state
  const [apiKeyValidation, setApiKeyValidation] = useState({
    isValid: true,
    errors: [],
    warnings: [],
  });
  const [userApiKeys, setUserApiKeys] = useState({});
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);

  // Use ref to track if we've initialized the form for this dialog session
  const initializedRef = useRef(false);

  // Fetch user API keys
  const fetchUserApiKeys = useCallback(async () => {
    try {
      const data = await apiKeysService.list();
      const keys = data.data?.apiKeys || data.apiKeys || {};
      setUserApiKeys(keys);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
    }
  }, []);

  // Validate API keys when form data changes
  const validateApiKeys = useCallback(() => {
    // Root agents, admins, and users/plans with "Use Root Credentials" skip BYOK checks
    const skip = usesRootCredentials({
      user,
      subscription,
      isRootAgent: formData.isRootAgent || editingAgent?.isRootAgent || isRootAgentMode,
    });
    if (skip) {
      setApiKeyValidation({ isValid: true, errors: [], warnings: [] });
      return;
    }
    const validation = validateAgentConfiguration(formData, userApiKeys);
    setApiKeyValidation(validation);
  }, [
    formData,
    userApiKeys,
    editingAgent?.isRootAgent,
    isRootAgentMode,
    user,
    subscription,
  ]);

  // Fetch user API keys when component mounts
  useEffect(() => {
    if (isOpen) {
      fetchUserApiKeys();
    }
  }, [isOpen, fetchUserApiKeys]);

  useEffect(() => {
    if (!isOpen) return;
    if (!editingAgent && (isBusiness || isAgency)) {
      setEditorTab('simple');
    } else {
      setEditorTab('advanced');
    }
  }, [isOpen, editingAgent, isBusiness, isAgency]);

  // Validate API keys when form data or user API keys change
  useEffect(() => {
    if (!isOpen) return;
    if (skipUserApiKeyValidation || formData.isRootAgent) {
      validateApiKeys();
      return;
    }
    if (Object.keys(userApiKeys).length > 0) {
      validateApiKeys();
    }
  }, [
    formData,
    userApiKeys,
    isOpen,
    validateApiKeys,
    skipUserApiKeyValidation,
  ]);

  useEffect(() => {
    // Only initialize form once when dialog first opens
    if (!isOpen) {
      initializedRef.current = false;
      return;
    }

    if (initializedRef.current) return;
    initializedRef.current = true;

    const findDefaultProvider = category => {
      const id = adminDefaults?.[category];
      if (!id) return '';
      const found = adminProviders.find(p => p._id === id && p.category === category);
      return found?.provider || '';
    };

    if (editingAgent) {
      setFormData({
        name: editingAgent.name || '',
        greeting: editingAgent.greeting || '',
        persona: editingAgent.persona || '',
        aiProvider: editingAgent.aiProvider || findDefaultProvider('ai_models') || '',
        aiModel: editingAgent.aiModel || '',
        aiModelOverride: editingAgent.aiModelOverride || '',
        voiceProvider: editingAgent.voiceProvider || findDefaultProvider('tts_models') || '',
        voiceId: editingAgent.voiceId || '',
        ttsModelOverride: editingAgent.ttsModelOverride || '',
        sttProvider: editingAgent.sttProvider || findDefaultProvider('stt_models') || '',
        sttModel: editingAgent.sttModel || '',
        sttModelOverride: editingAgent.sttModelOverride || '',
        sttLanguage: editingAgent.sttLanguage || 'en',
        havePresetQuestions: editingAgent.havePresetQuestions || false,
        presetQuestions: coercePresetQuestions(editingAgent.presetQuestions),
        repromptMessage:
          editingAgent.repromptMessage || "I didn't hear you. Could you please repeat?",
        useCase: editingAgent.useCase || 'marketing',
        customUseCase: editingAgent.customUseCase || '',
        enableRecording: editingAgent.enableRecording || false,
        enableTranscriptions: editingAgent.enableTranscriptions || false,
        isRootAgent: editingAgent.isRootAgent || false,
        ttsConfig: editingAgent.ttsConfig || { engine: 'neural' },
      });
    } else {
      setFormData({
        name: '',
        greeting: '',
        persona: '',
        aiProvider: findDefaultProvider('ai_models') || '',
        aiModel: '',
        aiModelOverride: '',
        voiceProvider: findDefaultProvider('tts_models') || '',
        voiceId: '',
        ttsModelOverride: '',
        sttProvider: findDefaultProvider('stt_models') || '',
        sttModel: '',
        sttModelOverride: '',
        sttLanguage: 'en',
        havePresetQuestions: false,
        presetQuestions: [],
        repromptMessage: "I didn't hear you. Could you please repeat?",
        useCase: 'marketing',
        customUseCase: '',
        enableRecording: false,
        enableTranscriptions: false,
        isRootAgent: isRootAgentMode || false,
        ttsConfig: { engine: 'neural' },
      });
    }
  }, [isOpen, editingAgent, isRootAgentMode]);

  const handleInputChange = useCallback(
    (field, value) => {
      // Prevent clearing model selections when Select component auto-fires with empty value
      if (
        (field === 'aiModel' || field === 'voiceId' || field === 'sttModel') &&
        value === '' &&
        formData[field]
      ) {
        return; // Don't clear model selections on initialization
      }
      setFormData(prev => ({ ...prev, [field]: value }));
    },
    [formData]
  );

  // Preset Questions handlers
  const handleAddPresetQuestion = () => {
    const current = coercePresetQuestions(formData.presetQuestions);
    if (current.length >= 10) {
      toast.error('Maximum 10 preset questions allowed');
      return;
    }
    setFormData(prev => ({
      ...prev,
      presetQuestions: [...coercePresetQuestions(prev.presetQuestions), ''],
    }));
  };

  const handleUpdatePresetQuestion = (index, value) => {
    setFormData(prev => ({
      ...prev,
      presetQuestions: coercePresetQuestions(prev.presetQuestions).map((q, i) =>
        i === index ? value : q
      ),
    }));
  };

  const handleDeletePresetQuestion = index => {
    setFormData(prev => ({
      ...prev,
      presetQuestions: coercePresetQuestions(prev.presetQuestions).filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const presetQuestions = coercePresetQuestions(formData.presetQuestions);

    // Validate preset questions
    if (formData.havePresetQuestions && presetQuestions.length === 0) {
      toast.error('Please add at least one preset question or disable preset questions');
      return;
    }

    // Filter out empty preset questions
    const filteredPresetQuestions = presetQuestions.filter(q => q.trim() !== '');
    if (formData.havePresetQuestions && filteredPresetQuestions.length === 0) {
      toast.error('Please add at least one valid preset question');
      return;
    }

    // Skip BYOK checks for root agents and users/plans on root credentials
    const skipKeys = usesRootCredentials({
      user,
      subscription,
      isRootAgent: formData.isRootAgent || editingAgent?.isRootAgent || isRootAgentMode,
    });
    if (!skipKeys) {
      const validation = validateAgentConfiguration(formData, userApiKeys);
      if (!validation.isValid) {
        setApiKeyValidation(validation);
        setShowApiKeyModal(true);
        return;
      }
    }

    // Get reCAPTCHA token
    const recaptchaToken = await getRecaptchaToken();

    // Submit with filtered questions and reCAPTCHA token
    onSubmit({
      ...formData,
      presetQuestions: filteredPresetQuestions,
      recaptchaToken: recaptchaToken || undefined,
    });
  };

  const normalizeModels = models => {
    if (!Array.isArray(models)) return [];
    return models
      .map(model => {
        if (!model) return null;
        if (typeof model === 'string') {
          return { id: model, name: model };
        }
        if (typeof model === 'object') {
          const id = model.id || model.value || model.key || model.name;
          const name = model.name || id;
          if (!id && !name) return null;
          return {
            id: id || name,
            name: name || id,
            supportsStreaming: model.supportsStreaming ?? model.streaming ?? false,
          };
        }
        const fallback = String(model);
        return { id: fallback, name: fallback };
      })
      .filter(Boolean);
  };

  const getModels = (category, provider) => {
    const source = adminProviders.length
      ? adminProviders
      : [
          ...(Array.isArray(providerCatalog.ai) ? providerCatalog.ai : []),
          ...(Array.isArray(providerCatalog.stt) ? providerCatalog.stt : []),
          ...(Array.isArray(providerCatalog.tts) ? providerCatalog.tts : []),
        ];
    const found = source.find(
      p => (p.category === category || category === undefined) && p.provider === provider
    );
    return normalizeModels(found?.models);
  };

  // Handle going to API keys settings
  const handleGoToSettings = () => {
    setShowApiKeyModal(false);
    onClose();

    const isRootAgent = formData.isRootAgent || editingAgent?.isRootAgent || isRootAgentMode;
    const isAdmin = user?.role === 'admin' || user?.role === 'super-admin';
    const onRootCreds = usesRootCredentials({ user, subscription, isRootAgent });

    // Root-credentials users cannot configure per-user keys — don't bounce them to a dead-end page
    if (onRootCreds && !isAdmin && !isRootAgent) {
      toast.success(
        'This account uses platform (root) credentials — you can save the agent without adding API keys.'
      );
      return;
    }

    // Lazy routes suspend; wrap navigation so a click doesn't sync-suspend the tree.
    if (isRootAgent || isAdmin) {
      startTransition(() => {
        navigate('/admin/root-provider-configs');
      });
      return;
    }

    startTransition(() => {
      navigate('/settings?tab=api-keys');
    });
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={open => {
          if (!open) {
            onClose();
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              {editingAgent
                ? 'Update your AI agent configuration'
                : 'Create a new AI agent with custom settings'}
            </DialogDescription>
          </DialogHeader>

          <Tabs value={editorTab} onValueChange={setEditorTab} className="w-full mb-2">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="simple">Simple</TabsTrigger>
              <TabsTrigger value="advanced">Advanced</TabsTrigger>
            </TabsList>
          </Tabs>

          <form onSubmit={handleSubmit} className="space-y-6">
            {editorTab === 'simple' && (
              <p className="text-sm text-muted-foreground -mt-2 mb-2 rounded-md border p-3 bg-muted/40">
                Simple mode shows essentials below. Open <strong>Advanced</strong> for STT, LLM, TTS,
                IVR, and recording options.
              </p>
            )}
            <div className="space-y-2">
              <Label htmlFor="name">Agent Name *</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={e => handleInputChange('name', e.target.value)}
                required
              />
            </div>

            {editorTab === 'advanced' && (user?.role === 'admin' || user?.role === 'super-admin') && (
              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border-2 border-purple-300 dark:border-purple-700 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="space-y-1 flex-1">
                    <Label
                      htmlFor="isRootAgent"
                      className="cursor-pointer font-semibold text-purple-900 dark:text-purple-100"
                    >
                      🎯{' '}
                      {editingAgent
                        ? 'Demo Agent Status'
                        : 'Create as Demo Agent (for Call Me Testing)'}
                    </Label>
                    <p className="text-sm text-purple-700 dark:text-purple-300">
                      {editingAgent
                        ? 'This is a demo agent for Call Me testing. It can be assigned to multiple users and uses root API credentials.'
                        : 'This agent will be available as a demo for Call Me testing. It can be assigned to multiple users and will use root API credentials.'}
                    </p>
                  </div>
                  <Switch
                    id="isRootAgent"
                    checked={formData.isRootAgent || false}
                    onCheckedChange={checked => handleInputChange('isRootAgent', checked)}
                    disabled={editingAgent && editingAgent.isRootAgent}
                  />
                </div>
                {formData.isRootAgent && (
                  <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                    <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">
                      ✓ Demo Agent Mode {editingAgent ? 'Enabled' : 'Enabled'} - This agent can be
                      assigned to users via Admin → User Edit page
                    </p>
                  </div>
                )}
              </div>
            )}

            {editorTab === 'advanced' && !formData.havePresetQuestions && (
              <>
                <ConditionalField featureKey="agent_form_persona" level="component">
                  <div className="space-y-2">
                    <Label htmlFor="persona">Agent Personality & Instructions *</Label>
                    <Textarea
                      id="persona"
                      value={formData.persona}
                      onChange={e => handleInputChange('persona', e.target.value)}
                      rows={4}
                      required
                      placeholder="e.g., Your name is Aariya. You are a friendly customer service representative..."
                    />
                    <p className="text-xs text-gray-500">
                      Be specific about the agent's name, role, and behavior. The AI will strictly
                      follow these instructions.
                    </p>
                  </div>
                </ConditionalField>

                {/* Use Case Section */}
                <ConditionalField featureKey="agent_form_use_case" level="component">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="useCase">Use Case *</Label>
                      <Select
                        value={formData.useCase}
                        onValueChange={v => handleInputChange('useCase', v)}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select use case" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="marketing">Marketing</SelectItem>
                          <SelectItem value="healthcare">Healthcare</SelectItem>
                          <SelectItem value="customer-service">Customer Service</SelectItem>
                          <SelectItem value="sales">Sales</SelectItem>
                          <SelectItem value="education">Education</SelectItem>
                          <SelectItem value="real-estate">Real Estate</SelectItem>
                          <SelectItem value="finance">Finance</SelectItem>
                          <SelectItem value="custom">Custom</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {formData.useCase === 'custom' && (
                      <div className="space-y-2">
                        <Label htmlFor="customUseCase">Custom Use Case</Label>
                        <Input
                          id="customUseCase"
                          type="text"
                          value={formData.customUseCase}
                          onChange={e => handleInputChange('customUseCase', e.target.value)}
                          placeholder="Describe your custom use case"
                        />
                      </div>
                    )}
                  </div>
                </ConditionalField>
              </>
            )}

            {editorTab === 'simple' && !formData.havePresetQuestions && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="simple-greeting-only">Greeting</Label>
                  <Input
                    id="simple-greeting-only"
                    value={formData.greeting}
                    onChange={e => handleInputChange('greeting', e.target.value)}
                    placeholder="Hello! How can I help you today?"
                  />
                </div>
                <ConditionalField featureKey="agent_form_persona" level="component">
                  <div className="space-y-2">
                    <Label htmlFor="simple-persona-only">Instructions *</Label>
                    <Textarea
                      id="simple-persona-only"
                      value={formData.persona}
                      onChange={e => handleInputChange('persona', e.target.value)}
                      rows={4}
                      required
                    />
                  </div>
                </ConditionalField>
                <ConditionalField featureKey="agent_form_use_case" level="component">
                  <div className="space-y-2">
                    <Label>Use case</Label>
                    <Select value={formData.useCase} onValueChange={v => handleInputChange('useCase', v)}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select use case" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="marketing">Marketing</SelectItem>
                        <SelectItem value="customer-service">Customer Service</SelectItem>
                        <SelectItem value="sales">Sales</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </ConditionalField>
              </>
            )}

            {editorTab === 'advanced' && (
            <>
            {/* Language Section */}
            <ConditionalField featureKey="agent_form_language" level="component">
              <div className="space-y-2">
                <Label htmlFor="sttLanguage">Language *</Label>
                <Select
                  value={formData.sttLanguage}
                  onValueChange={v => handleInputChange('sttLanguage', v)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="hi">Hindi</SelectItem>
                    <SelectItem value="es">Spanish</SelectItem>
                    <SelectItem value="fr">French</SelectItem>
                    <SelectItem value="de">German</SelectItem>
                    <SelectItem value="it">Italian</SelectItem>
                    <SelectItem value="pt">Portuguese</SelectItem>
                    <SelectItem value="ja">Japanese</SelectItem>
                    <SelectItem value="ko">Korean</SelectItem>
                    <SelectItem value="ar">Arabic</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  TTS language selection will also impact on the agent behaviour. Try to match both
                  language settings for optimal performance.
                </p>
              </div>
            </ConditionalField>

            {editorTab === 'advanced' && (
            <ConditionalField featureKey="agent_form_preset_questions" level="component">
              <div className="space-y-3 p-4 border rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="havePresetQuestions"
                      checked={formData.havePresetQuestions}
                      onCheckedChange={checked => handleInputChange('havePresetQuestions', checked)}
                    />
                    <Label htmlFor="havePresetQuestions" className="cursor-pointer">
                      Want IVR ?
                    </Label>
                  </div>
                </div>

                {/* Greeting */}
                <div className="space-y-2">
                  <Label htmlFor="greeting">Greeting Message</Label>
                  <Input
                    id="greeting"
                    type="text"
                    value={formData.greeting}
                    onChange={e => handleInputChange('greeting', e.target.value)}
                    placeholder="Hello! How can I help you today?"
                  />
                  <p className="text-xs text-gray-500">
                    The first message the agent speaks when a call connects.
                  </p>
                </div>

                {formData.havePresetQuestions && (
                  <div className="space-y-3 mt-4">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                      <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-yellow-800">
                            Important: Greeting Behavior
                          </p>
                          <p className="text-sm text-yellow-700 mt-1">
                            When preset questions are enabled, the default greeting will be skipped.
                            The agent will start immediately by asking the first question. Make sure
                            your first question serves as an appropriate introduction.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Reprompt Message */}
                    <ConditionalField featureKey="agent_form_reprompt_message" level="component">
                      <div className="space-y-2">
                        <Label htmlFor="repromptMessage">Reprompt Message</Label>
                        <Input
                          id="repromptMessage"
                          type="text"
                          value={formData.repromptMessage}
                          onChange={e => handleInputChange('repromptMessage', e.target.value)}
                          placeholder="I didn't hear you. Could you please repeat?"
                        />
                        <p className="text-xs text-gray-500">
                          Message to play when user doesn't respond or response is unclear. This
                          will be used for retries.
                        </p>
                      </div>
                    </ConditionalField>

                    <p className="text-sm text-gray-600">
                      Agent will ask these questions one by one. Maximum 10 questions.
                    </p>
                    {coercePresetQuestions(formData.presetQuestions).map((question, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          value={question}
                          onChange={e => handleUpdatePresetQuestion(index, e.target.value)}
                          placeholder={`Question ${index + 1}`}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => handleDeletePresetQuestion(index)}
                          disabled={coercePresetQuestions(formData.presetQuestions).length === 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    {coercePresetQuestions(formData.presetQuestions).length < 10 && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleAddPresetQuestion}
                        className="w-full"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Question
                      </Button>
                    )}
                    {coercePresetQuestions(formData.presetQuestions).length === 0 && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleAddPresetQuestion}
                        className="w-full"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add First Question
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </ConditionalField>
            )}

            {/* Recording & Transcriptions Section */}
            {editorTab === 'advanced' && (
            <div className="space-y-4 p-4 border rounded-lg">
              <h3 className="text-sm font-semibold">Call Features</h3>

              <div className="space-y-4">
                <ConditionalField featureKey="agent_form_recording" level="component">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="enableRecording"
                      checked={formData.enableRecording}
                      onCheckedChange={checked => handleInputChange('enableRecording', checked)}
                    />
                    <div className="flex-1 space-y-1">
                      <Label htmlFor="enableRecording" className="cursor-pointer font-normal">
                        Enable Recording
                      </Label>
                      {formData.enableRecording && (
                        <p className="text-xs text-gray-500 mt-1">
                          Configure Zapier webhooks in{' '}
                          <a
                            href="/integrations?tab=zapier"
                            className="text-blue-600 hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Integrations → Zapier
                          </a>{' '}
                          to receive recordings.
                        </p>
                      )}
                    </div>
                  </div>
                </ConditionalField>

                <ConditionalField featureKey="agent_form_transcriptions" level="component">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="enableTranscriptions"
                      checked={formData.enableTranscriptions}
                      onCheckedChange={checked =>
                        handleInputChange('enableTranscriptions', checked)
                      }
                    />
                    <div className="flex-1 space-y-1">
                      <Label htmlFor="enableTranscriptions" className="cursor-pointer font-normal">
                        Enable Transcriptions
                      </Label>
                      {formData.enableTranscriptions && (
                        <p className="text-xs text-gray-500 mt-1">
                          Configure Zapier webhooks in{' '}
                          <a
                            href="/integrations?tab=zapier"
                            className="text-blue-600 hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Integrations → Zapier
                          </a>{' '}
                          to receive transcriptions.
                        </p>
                      )}
                    </div>
                  </div>
                </ConditionalField>
              </div>
            </div>
            )}

            {editorTab === 'advanced' && !formData.havePresetQuestions && (
              <ConditionalField featureKey="agent_form_ai_provider" level="component">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="aiProvider">AI Provider</Label>
                    <Select
                      value={formData.aiProvider}
                      onValueChange={v => handleInputChange('aiProvider', v)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select AI provider" />
                      </SelectTrigger>
                      <SelectContent>
                        {aiProviders.map(p => (
                          <SelectItem key={p.provider} value={p.provider}>
                            {p.provider}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <ConditionalField featureKey="agent_form_ai_model" level="component">
                    <div className="space-y-2">
                      <Label htmlFor="aiModel">AI Model</Label>
                      <Select
                        value={formData.aiModel}
                        onValueChange={v => handleInputChange('aiModel', v)}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select AI model" />
                        </SelectTrigger>
                        <SelectContent>
                          {getModels('ai_models', formData.aiProvider).map(m => (
                            <SelectItem key={m.id || m.name} value={m.id || m.name}>
                              {m.name || m.id}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <ConditionalField
                        featureKey="agent_form_ai_model_override"
                        level="component"
                        defaultValue={false}
                      >
                        <div className="mt-2">
                          <Label className="text-xs text-gray-500">Optional Override Model</Label>
                          <Input
                            placeholder="Custom AI model (optional)"
                            value={formData.aiModelOverride || ''}
                            onChange={e => handleInputChange('aiModelOverride', e.target.value)}
                          />
                        </div>
                      </ConditionalField>
                    </div>
                  </ConditionalField>
                </div>
              </ConditionalField>
            )}

            {/* TTS and STT Provider Sections */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ConditionalField featureKey="agent_form_tts_provider" level="component">
                <div className="space-y-2">
                  <Label htmlFor="voiceProvider">TTS Provider</Label>
                  <Select
                    value={formData.voiceProvider}
                    onValueChange={v => handleInputChange('voiceProvider', v)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select TTS provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {ttsProviders.map(p => (
                        <SelectItem key={p.provider} value={p.provider}>
                          {p.provider}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </ConditionalField>
              <ConditionalField featureKey="agent_form_tts_model" level="component">
                <div className="space-y-2">
                  <Label htmlFor="voiceId">Voice / TTS Model</Label>
                  <Select
                    value={formData.voiceId}
                    onValueChange={v => handleInputChange('voiceId', v)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select voice / model" />
                    </SelectTrigger>
                    <SelectContent>
                      {getModels('tts_models', formData.voiceProvider).map(m => (
                        <SelectItem key={m.id || m.name} value={m.id || m.name}>
                          {m.name || m.id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* AWS Polly Engine Selection */}
                  {(formData.voiceProvider === 'polly' || formData.voiceProvider === 'amazon_polly' || formData.voiceProvider === 'aws') && (
                    <div className="mt-2 space-y-2">
                      <Label htmlFor="pollyEngine" className="text-xs">Polly Speech Engine</Label>
                      <Select
                        value={formData.ttsConfig?.engine || 'neural'}
                        onValueChange={v => handleInputChange('ttsConfig', { ...formData.ttsConfig, engine: v })}
                      >
                        <SelectTrigger className="w-full h-8 text-xs">
                          <SelectValue placeholder="Select engine" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="neural">Neural (High Quality)</SelectItem>
                          <SelectItem value="standard">Standard</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <ConditionalField
                    featureKey="agent_form_tts_model_override"
                    level="component"
                    defaultValue={false}
                  >
                    <div className="mt-2">
                      <Label className="text-xs text-gray-500">Optional Override Model</Label>
                      <Input
                        placeholder="Custom TTS model (optional)"
                        value={formData.ttsModelOverride || ''}
                        onChange={e => handleInputChange('ttsModelOverride', e.target.value)}
                      />
                    </div>
                  </ConditionalField>
                </div>
              </ConditionalField>
              <ConditionalField featureKey="agent_form_stt_provider" level="component">
                <div className="space-y-2">
                  <Label htmlFor="sttProvider">STT Provider</Label>
                  <Select
                    value={formData.sttProvider}
                    onValueChange={v => handleInputChange('sttProvider', v)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select STT provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {sttProviders.map(p => (
                        <SelectItem key={p.provider} value={p.provider}>
                          {p.provider}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </ConditionalField>
              <ConditionalField featureKey="agent_form_stt_model" level="component">
                <div className="space-y-2">
                  <Label htmlFor="sttModel">STT Model</Label>
                  <Select
                    value={formData.sttModel || ''}
                    onValueChange={v => handleInputChange('sttModel', v)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select STT model" />
                    </SelectTrigger>
                    <SelectContent>
                      {getModels('stt_models', formData.sttProvider).map(m => (
                        <SelectItem key={m.id || m.name} value={m.id || m.name}>
                          {m.name || m.id}
                          {m.supportsStreaming && (
                            <span className="ml-2 text-xs text-emerald-500 font-semibold">
                              ⚡ Streaming
                            </span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <ConditionalField
                    featureKey="agent_form_stt_model_override"
                    level="component"
                    defaultValue={false}
                  >
                    <div className="mt-2">
                      <Label className="text-xs text-gray-500">Optional Override Model</Label>
                      <Input
                        placeholder="Custom STT model (optional)"
                        value={formData.sttModelOverride || ''}
                        onChange={e => handleInputChange('sttModelOverride', e.target.value)}
                      />
                    </div>
                  </ConditionalField>
                </div>
              </ConditionalField>
            </div>
            </>
            )}

            {/* API Key Validation Display */}
            {!apiKeyValidation.isValid && apiKeyValidation.errors.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-red-800 dark:text-red-300 mb-2">
                      API Key Configuration Required
                    </h4>
                    <div className="text-sm text-red-700 dark:text-red-300 space-y-1">
                      {apiKeyValidation.errors.map((error, index) => (
                        <div key={index}>• {error}</div>
                      ))}
                    </div>
                    <div className="mt-3">
                      {formData.isRootAgent ||
                      editingAgent?.isRootAgent ||
                      isRootAgentMode ||
                      user?.role === 'admin' ||
                      user?.role === 'super-admin' ? (
                        <a
                          href="/admin/root-provider-configs"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          Configure Root Provider Configs
                        </a>
                      ) : (
                        <a
                          href="/settings?tab=api-keys"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          Configure API Keys
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {(skipUserApiKeyValidation || formData.isRootAgent) && (
              <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-100">
                This account uses platform (root) credentials — personal API keys are not required
                to create or edit this agent.
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                className="btn-secondary-glass"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="btn-primary-gradient"
                disabled={!apiKeyValidation.isValid}
                title={!apiKeyValidation.isValid ? 'Please configure required API keys first' : ''}
              >
                {editingAgent ? 'Update Agent' : 'Create Agent'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* API Key Validation Modal */}
      <ApiKeyValidationModal
        isOpen={showApiKeyModal}
        onClose={() => setShowApiKeyModal(false)}
        validationErrors={apiKeyValidation.errors}
        onGoToSettings={handleGoToSettings}
      />
    </>
  );
};

export default AgentForm;
